import { Controller, useForm, FormProvider, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslations } from "use-intl";
import { Plus, Trash2 } from "lucide-react";
import { FormField } from "@/components/form/FormField";
import { useUpdateStep } from "@/hooks/api/mutations/workflows.mutations";
import { useWorkflowSteps } from "@/hooks/api/queries/workflows.queries";
import type { WorkflowStep } from "@/types/workflow.types";
import { AgenticModelSelect } from "./agentic-model-select";
import { HighlightedCodeEditor } from "./highlighted-code-editor";

const branchSchema = z.object({
  label: z.string().min(1, "Label required"),
  conditionText: z.string().min(1, "Condition required"),
  targetAlias: z.string().min(1, "Target step required"),
});

const schema = z.object({
  modelId: z.string().min(1, "Model required"),
  prompt: z.string().min(1, "Prompt required"),
  reasoningVisibility: z.enum(["always", "on_demand", "hidden"]),
  branches: z.array(branchSchema).min(2, "At least 2 branches required"),
});

type FormValues = z.infer<typeof schema>;

function toFormValues(config: Record<string, unknown>): FormValues {
  const branches = Array.isArray(config.branches)
    ? (config.branches as Array<Record<string, string>>).map((b) => ({
        label: String(b.label ?? ""),
        conditionText: String(b.condition_text ?? ""),
        targetAlias: String(b.target_alias ?? ""),
      }))
    : [
        { label: "", conditionText: "", targetAlias: "" },
        { label: "", conditionText: "", targetAlias: "" },
      ];

  return {
    modelId: String(config.model_id ?? ""),
    prompt: String(config.prompt ?? ""),
    reasoningVisibility: (["always", "on_demand", "hidden"].includes(String(config.reasoning_visibility))
      ? config.reasoning_visibility
      : "on_demand") as "always" | "on_demand" | "hidden",
    branches,
  };
}

function toPayload(values: FormValues): Record<string, unknown> {
  return {
    model_id: values.modelId,
    prompt: values.prompt,
    reasoning_visibility: values.reasoningVisibility,
    branches: values.branches.map((b) => ({
      label: b.label,
      condition_text: b.conditionText,
      target_alias: b.targetAlias,
    })),
  };
}

interface AgentDecisionConfigFormProps {
  step: WorkflowStep;
  workflowId: string;
  onSuccess?: () => void;
}

export function AgentDecisionConfigForm({ step, workflowId, onSuccess }: AgentDecisionConfigFormProps) {
  const t = useTranslations("workflows");
  const updateStep = useUpdateStep();
  const { data: stepsData } = useWorkflowSteps(workflowId);
  const peerSteps = (stepsData?.data ?? []).filter((s) => s.id !== step.id);

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: toFormValues(step.config),
  });

  const { handleSubmit, register, control, formState: { errors, isSubmitting } } = methods;
  const { fields, append, remove } = useFieldArray({ control: methods.control, name: "branches" });

  const onSubmit = handleSubmit(async (values) => {
    await updateStep.mutateAsync({ workflowId, stepId: step.id, config: toPayload(values) });
    onSuccess?.();
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} className="flex flex-col gap-5">
        <AgenticModelSelect
          name="modelId"
          label={t("drawer.field.model_id")}
          required
        />

        <FormField label={t("drawer.field.prompt")} required error={errors.prompt?.message}>
          <Controller
            control={control}
            name="prompt"
            render={({ field }) => (
              <HighlightedCodeEditor
                language="markdown"
                value={field.value}
                onChange={(event) => field.onChange(event.target.value)}
                onBlur={field.onBlur}
                rows={5}
                minHeight={126}
                placeholder="Analyze the input and decide which branch to take…"
                invalid={!!errors.prompt}
              />
            )}
          />
        </FormField>

        <FormField label={t("drawer.field.reasoning_visibility")} error={errors.reasoningVisibility?.message}>
          <select
            {...register("reasoningVisibility")}
            className="h-9 w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 text-sm focus:border-[var(--ring)] focus:outline-none"
          >
            <option value="always">Always visible</option>
            <option value="on_demand">On demand</option>
            <option value="hidden">Hidden</option>
          </select>
        </FormField>

        {/* Branches */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-[var(--foreground)]">{t("drawer.field.branches")}</span>
            <button
              type="button"
              onClick={() => append({ label: "", conditionText: "", targetAlias: "" })}
              className="flex items-center gap-1 text-xs text-[var(--primary)] hover:underline"
            >
              <Plus className="size-3" /> Add branch
            </button>
          </div>
          {errors.branches?.root && (
            <p className="text-xs text-[var(--destructive)]">{errors.branches.root.message}</p>
          )}
          {fields.map((field, i) => (
            <div key={field.id} className="rounded-lg border border-[var(--border)] p-3 flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-[var(--muted-foreground)]">Branch {i + 1}</span>
                {fields.length > 2 && (
                  <button type="button" onClick={() => remove(i)} className="text-[var(--destructive)] hover:opacity-70">
                    <Trash2 className="size-3.5" />
                  </button>
                )}
              </div>
              <input
                {...register(`branches.${i}.label`)}
                placeholder="Label (e.g. Approved)"
                className="h-8 w-full rounded border border-[var(--border)] bg-[var(--card)] px-2.5 text-sm focus:outline-none"
              />
              <input
                {...register(`branches.${i}.conditionText`)}
                placeholder="Condition (natural language)"
                className="h-8 w-full rounded border border-[var(--border)] bg-[var(--card)] px-2.5 text-sm focus:outline-none"
              />
              <select
                {...register(`branches.${i}.targetAlias`)}
                className="h-8 w-full rounded border border-[var(--border)] bg-[var(--card)] px-2.5 text-sm focus:outline-none"
              >
                <option value="">Select target step…</option>
                {peerSteps.map((s) => (
                  <option key={s.id} value={s.stepName}>{s.stepName}</option>
                ))}
              </select>
            </div>
          ))}
        </div>

        {updateStep.isError && (
          <p className="text-xs text-[var(--destructive)]">{t("drawer.save_error")}</p>
        )}

        <button
          type="submit"
          disabled={isSubmitting || updateStep.isPending}
          className="h-9 w-full rounded-lg bg-[var(--primary)] text-sm font-medium text-[var(--primary-foreground)] hover:opacity-90 disabled:opacity-60"
        >
          {isSubmitting || updateStep.isPending ? t("drawer.saving") : t("drawer.save")}
        </button>
      </form>
    </FormProvider>
  );
}

export default AgentDecisionConfigForm;
